import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthVerify() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');
      const returnTo = searchParams.get('returnTo') || '/dashboard';

      if (!token) {
        setStatus('error');
        setError(t('auth.noToken', 'No verification token provided'));
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/magic-link/verify?token=${token}`, {
          withCredentials: true
        });
        
        setStatus('success');
        
        // Redirect after short delay
        setTimeout(() => {
          navigate(response.data.return_to || returnTo);
        }, 1500);
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || t('auth.verifyError', 'Failed to verify magic link'));
      }
    };

    verifyToken();
  }, [searchParams, navigate, t]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-rose-50/30 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-heading font-bold text-primary">
            🧶 Even Rounds
          </Link>
        </div>

        <div className="bg-white rounded-3xl border border-stone-100 p-8 shadow-sm text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-heading font-bold mb-2">
                {t('auth.verifying', 'Verifying...')}
              </h1>
              <p className="text-muted-foreground">
                {t('auth.pleaseWait', 'Please wait while we sign you in.')}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2 text-green-700">
                {t('auth.success', 'Signed in!')}
              </h1>
              <p className="text-muted-foreground">
                {t('auth.redirecting', 'Redirecting you now...')}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2 text-red-700">
                {t('auth.failed', 'Sign in failed')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {error}
              </p>
              <div className="space-y-3">
                <Button asChild className="w-full rounded-full">
                  <Link to="/login">{t('auth.tryAgain', 'Try again')}</Link>
                </Button>
                <Button asChild variant="outline" className="w-full rounded-full">
                  <Link to="/">{t('common.backToHome', 'Back to Home')}</Link>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
